import { Link } from 'react-router';

import { InlineNotice } from '@/components/common';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/utils';

import { useMySubscription } from '../hooks/useMySubscription';
import { DUNNING_GRACE_DAYS, REMAINING_DAYS_THRESHOLD } from '../lib/status';

/**
 * 만료·임박·유예(dunning) 배너 — readOnly(만료) 또는 잔여 ≤3일 또는 PAST_DUE 시 노출.
 * 대시보드 상단 등 워크스페이스 진입 지점에 배치한다. 'silent' 조회라 실패 시 조용히 숨는다.
 * PAST_DUE 는 서비스 유지 상태이므로 만료·임박보다 먼저 판정해 결제수단 안내를 우선한다.
 */
export function SubscriptionExpiryBanner({ className }: { className?: string }) {
  const { data } = useMySubscription('silent');
  if (!data) return null;

  const isPastDue = data.status === 'PAST_DUE';
  const visible = isPastDue || data.readOnly || data.remainingDays <= REMAINING_DAYS_THRESHOLD;
  if (!visible) return null;

  const tone = data.readOnly && !isPastDue ? 'destructive' : 'warning';
  const message = isPastDue
    ? `결제에 실패해 유예 기간(${DUNNING_GRACE_DAYS}일) 중입니다. 카드 정보를 확인하거나 교체해 주세요.`
    : data.readOnly
      ? '구독이 만료되어 일부 기능 이용이 제한됩니다.'
      : `구독이 ${data.remainingDays}일 후 만료됩니다.`;

  return (
    <InlineNotice tone={tone} className={cn('mb-4', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>{message}</span>
        <Button variant="link" size="sm" asChild className="h-auto min-h-10 p-0 text-sm">
          <Link to={ROUTES.workspace.subscription}>
            {isPastDue ? '결제수단 교체' : '내 구독 확인'}
          </Link>
        </Button>
      </div>
    </InlineNotice>
  );
}
